import { css } from 'aphrodite';
import * as React from 'react';
import { LoadingSpinner } from '../LoadingSpinner';
import { CalendarViewToolbar } from './components/CalendarViewToolbar';
import { MonthView } from './components/MonthView';
import { TimeGridView } from './components/TimeGridView';
import { styleSheet } from './styles';
import { CalendarItem } from './types';
import { CalendarNavigation } from './useCalendarNavigation';

interface IProps {
	nav: CalendarNavigation;
	/** Normalized items for the visible window (the caller fetches by `nav.range` and maps them). */
	items: CalendarItem[];
	isLoading?: boolean;
	/** The caller opens its own flyout / navigates for the selected item. */
	onSelectItem: (item: CalendarItem) => void;
	/** More undated action items exist than were fetched (drives today's "N+ more" roll-up). */
	undatedHasMore?: boolean;
	/** Replace the default (minimal) toolbar — e.g. the rich My Calendar toolbar with filters. */
	renderToolbar?: (nav: CalendarNavigation) => React.ReactNode;
	toolbarTitleTag?: 'h1' | 'h2';
}

/**
 * The shared calendar surface: renders a header (navigation + month/week/day switch) above the
 * month grid or time grid, driven by {@link useCalendarNavigation}. It is data-source-agnostic —
 * callers fetch whatever they want for `nav.range`, map it to {@link CalendarItem}s, and handle
 * selection themselves — so the same component powers the My Calendar page, the meetings board,
 * and the action-items view.
 */
export const CalendarView: React.FC<IProps> = ({
	nav,
	items,
	isLoading = false,
	onSelectItem,
	undatedHasMore = false,
	renderToolbar,
	toolbarTitleTag = 'h2',
}) => {
	return (
		<>
			{renderToolbar ? (
				renderToolbar(nav)
			) : (
				<CalendarViewToolbar
					viewMode={nav.viewMode}
					onViewModeChange={nav.onViewModeChange}
					title={nav.title}
					onPrevious={nav.onPrevious}
					onNext={nav.onNext}
					onToday={nav.onToday}
					titleTag={toolbarTitleTag}
				/>
			)}

			<div className={css(styleSheet.viewBody)}>
				{isLoading ? (
					<div className={css(styleSheet.loadingOverlay)}>
						<LoadingSpinner type='large' />
					</div>
				) : nav.viewMode === 'month' ? (
					<MonthView
						focusedDate={nav.focusedDate}
						days={nav.days}
						items={items}
						onSelectItem={onSelectItem}
						onSelectDate={nav.onSelectDate}
						undatedHasMore={undatedHasMore}
					/>
				) : (
					<TimeGridView
						days={nav.days}
						items={items}
						onSelectItem={onSelectItem}
						onSelectDate={nav.onSelectDate}
						undatedHasMore={undatedHasMore}
					/>
				)}
			</div>
		</>
	);
};
